import { useState, useEffect } from 'react';
import FormInput from '../ui/FormInput';
import Button from '../ui/Button';
import { EmailInput } from '../input';
import { useUpdateProfileMutation } from '../../redux/apis/endpoints/profileApi';

const Info = ({ user, formData, setFormData, userLang, refetchUser }) => {
  const [updateProfile, { isLoading: isSubmitting }] = useUpdateProfileMutation();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({
    email: []
  });
  // Store initial form data for comparison
  const [initialFormData, setInitialFormData] = useState({});
  // Track if the form has changes
  const [hasChanges, setHasChanges] = useState(false);

  // Capture initial form data when user data is loaded
  useEffect(() => {
    if (user) {
      const initialData = {
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        email: user.email || '',
        bio: user.bio || ''
      };
      setInitialFormData(initialData);
    }
  }, [user]);

  // Check for changes whenever formData changes
  useEffect(() => {
    if (Object.keys(initialFormData).length > 0) {
      const hasAnyChanges = 
        formData.firstname !== initialFormData.firstname ||
        formData.lastname !== initialFormData.lastname ||
        formData.email !== initialFormData.email ||
        formData.bio !== initialFormData.bio;
      
      setHasChanges(hasAnyChanges);
    }
  }, [formData, initialFormData]);

  // Log form data changes for debugging
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Form data:', formData);
      console.log('Initial data:', initialFormData);
      console.log('Has changes:', hasChanges);
    }
  }, [formData, initialFormData, hasChanges]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailValidationChange = (errors) => {
    setValidationErrors(prev => ({
      ...prev,
      email: errors
    }));
  };

  // Handle account information update
  const handleAccountUpdate = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    // Check for validation errors
    if (validationErrors.email.length > 0) {
      setErrorMessage(userLang === 'zh-CN' ? '请修正表单中的错误' : 'Please fix errors in the form');
      return;
    }

    try {
      const userData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        bio: formData.bio
      };

      // Check if bio is empty string and convert to null to ensure proper API handling
      if (userData.bio === '') {
        userData.bio = null;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log('Submitting user data:', userData);
      }

      const response = await updateProfile(userData).unwrap();
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('Update response:', response);
      }
      
      setSuccessMessage(userLang === 'zh-CN' ? '个人信息已更新' : 'Personal information successfully updated');
      setInitialFormData(userData); // Update initial data
      setHasChanges(false); // Reset changes flag
      refetchUser();
    } catch (error) {
      console.error("Info update error:", error);
      setErrorMessage(
        error.data?.message || error.data?.error?.message || 
        (userLang === 'zh-CN' ? '更新失败' : 'Failed to update personal information')
      );
    }
  };

  return (
    <>
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-md">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
          {errorMessage}
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {userLang === 'zh-CN' ? '个人信息' : 'Personal Information'}
        </h3>
        <form onSubmit={handleAccountUpdate} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormInput
              label={userLang === 'zh-CN' ? '名' : 'First Name'}
              name="firstname"
              value={formData.firstname || ''}
              onChange={handleInputChange}
            />
            <FormInput
              label={userLang === 'zh-CN' ? '姓' : 'Last Name'}
              name="lastname"
              value={formData.lastname || ''}
              onChange={handleInputChange}
            />
            <EmailInput
              label={userLang === 'zh-CN' ? '邮箱' : 'Email'}
              name="email"
              value={formData.email || ''}
              onChange={handleInputChange}
              userLang={userLang}
              onValidationChange={handleEmailValidationChange}
            />
            {/* Phone number temporarily commented out
            <PhoneInput
              label={userLang === 'zh-CN' ? '电话' : 'Phone'}
              name="phonenumber"
              value={formData.phonenumber}
              onChange={handleInputChange}
              userLang={userLang}
              onValidationChange={handlePhoneValidationChange}
            />
            */}
          </div>
          
          {/* Bio field */}
          <div>
            <FormInput
              as="textarea"
              label={userLang === 'zh-CN' ? '个人简介' : 'Bio'}
              name="bio"
              value={formData.bio || ''}
              onChange={handleInputChange}
              placeholder={userLang === 'zh-CN' ? '介绍一下自己...' : 'Tell us about yourself...'}
              rows={4}
            />
          </div>
          
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isSubmitting || !hasChanges}
              isLoading={isSubmitting}
              fullWidth={false}
              variant="teal"
              className="px-6"
            >
              {userLang === 'zh-CN' ? '保存' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Info; 